// One billet. Timer is the milliseconds between this billet entering H1 and the
// next one entering it; Speed is the average speed it left H1 at.
export interface TimerSpeedPoint {
	ProductSizeId: number | null;
	ProductName: string;
	Timer: number;
	Speed: number | null;
}

// What each axis actually measures, written where an operator can read it. Both
// were checked against the procedures that fill the columns, not inferred from
// the axis titles: Timer is a gap BETWEEN billets, not the time one billet spends
// in the stand, and Speed is an average over the pass, not the speed on the way
// out.
export const AXIS_HELP = {
	Timer: "Time between this billet entering H1 and the next one entering it.",
	Speed: "Average speed of H1 while this billet was rolling.",
} as const;

// The legacy chart nails both axes to these bounds and silently drops whatever
// falls outside. Measured on four months of production data, that is 23.6% of the
// rows — all of it on the Timer axis or missing Speed, nothing on the Speed axis.
export const TIMER_AXIS_MIN = 30_000;
export const TIMER_AXIS_MAX = 100_000;
export const SPEED_AXIS_MIN = 35;
export const SPEED_AXIS_MAX = 120;

export const isPlottable = (point: TimerSpeedPoint): boolean =>
	point.Speed != null &&
	point.Timer >= TIMER_AXIS_MIN &&
	point.Timer <= TIMER_AXIS_MAX &&
	point.Speed >= SPEED_AXIS_MIN &&
	point.Speed <= SPEED_AXIS_MAX;

// The ten the legacy screen starts from, kept so the same product keeps roughly
// the colour an operator is used to. Past the tenth the legacy walks a rainbow
// scale; the mill runs 133 products, so any palette repeats and the legend is
// what actually identifies a series.
const BASE_COLORS = [
	"#2222de",
	"#e68a0a",
	"#279e16",
	"#d6300f",
	"#8081e4",
	"#dc6c14",
	"#de8fdd",
	"#a7a6a6",
	"#a8c32b",
	"#2dbcd0",
];

export const colorForIndex = (index: number): string => {
	if (index < BASE_COLORS.length) return BASE_COLORS[index];
	// Golden-angle steps keep consecutive products far apart in hue instead of
	// handing neighbouring series two shades of the same colour.
	const hue = ((index - BASE_COLORS.length) * 137.508) % 360;
	return `hsl(${hue.toFixed(1)}, 65%, 45%)`;
};
